import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { colors } from '../theme';

const Message = ({ message, isUser, timestamp, type, isSynced = false, isMobile = false }) => {

  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('zh-CN', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const renderedMessage = useMemo(() => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          code({node, inline, className, children, ...props}) {
            const isCodeBlock = className || (children && children[0] && children[0].value && children[0].value.includes('\n'));
            
            const textContent = children && children[0] ? children[0].value : '';
            
            if (isCodeBlock || (textContent && textContent.includes('\n'))) {
              return (
                <pre 
                  style={{ 
                    backgroundColor: isUser ? 'rgba(255, 255, 255, 0.13)' : 'rgba(0, 0, 0, 0.05)', 
                    padding: isMobile ? '10px 12px' : '14px 16px', 
                    borderRadius: '8px',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    overflowX: 'auto',
                    border: isUser ? '1px solid rgba(255, 240, 240, 0.25)' : '1px solid rgba(0, 0, 0, 0.1)',
                    margin: isMobile ? '8px 0' : '12px 0',
                    fontSize: isMobile ? '0.8rem' : '0.85rem',
                  }}
                >
                  <code 
                    style={{ 
                      fontSize: isMobile ? '0.8rem' : '0.85rem', 
                      lineHeight: 1.6 
                    }} 
                    {...props}
                  >
                    {children}
                  </code>
                </pre>
              );
            } else {
              return (
                <code 
                  style={{ 
                    backgroundColor: isUser ? 'rgba(255, 255, 255, 0.13)' : 'rgba(0, 0, 0, 0.05)', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    border: isUser ? '1px solid rgba(255, 240, 240, 0.25)' : '1px solid rgba(0, 0, 0, 0.1)',
                    fontSize: isMobile ? '0.85rem' : '0.9rem'
                  }} 
                  {...props}
                >
                  {children}
                </code>
              );
            }
          },
          a({node, ...props}) {
            return (
              <a 
                style={{ 
                  color: isUser ? '#ffe0e0' : colors.primary,
                  textDecoration: 'underline',
                  wordBreak: 'break-all'
                }} 
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              />
            );
          },
          h1({node, ...props}) {
            return <h1 style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 'bold', margin: isMobile ? '12px 0 6px 0' : '16px 0 8px 0' }} {...props} />;
          },
          h2({node, ...props}) {
            return <h2 style={{ fontSize: isMobile ? '1.15rem' : '1.3rem', fontWeight: 'bold', margin: isMobile ? '10px 0 5px 0' : '14px 0 7px 0' }} {...props} />;
          },
          h3({node, ...props}) {
            return <h3 style={{ fontSize: isMobile ? '1.05rem' : '1.1rem', fontWeight: 'bold', margin: isMobile ? '8px 0 4px 0' : '12px 0 6px 0' }} {...props} />;
          },
          p({node, ...props}) {
            return <p style={{ margin: isMobile ? '6px 0' : '8px 0' }} {...props} />;
          },
          ul({node, ...props}) {
            return <ul style={{ paddingLeft: isMobile ? '16px' : '20px', margin: isMobile ? '6px 0' : '8px 0' }} {...props} />;
          },
          ol({node, ...props}) {
            return <ol style={{ paddingLeft: isMobile ? '16px' : '20px', margin: isMobile ? '6px 0' : '8px 0' }} {...props} />;
          },
          li({node, ...props}) {
            return <li style={{ margin: isMobile ? '3px 0' : '4px 0' }} {...props} />;
          },
          blockquote({node, ...props}) {
            return (
              <blockquote 
                style={{ 
                  borderLeft: `3px solid ${isUser ? 'rgba(255, 240, 240, 0.4)' : colors.primary}`,
                  paddingLeft: isMobile ? '12px' : '16px', 
                  margin: isMobile ? '6px 0' : '8px 0',
                  fontStyle: 'italic'
                }} 
                {...props}
              />
            );
          },
          table({node, ...props}) {
            return (
              <div style={{ overflowX: 'auto', margin: isMobile ? '8px 0' : '12px 0' }}>
                <table 
                  style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    border: `1px solid ${isUser ? 'rgba(255, 240, 240, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
                    fontSize: isMobile ? '0.85rem' : '0.9rem'
                  }} 
                  {...props}
                />
              </div>
            );
          },
          th({node, ...props}) {
            return (
              <th 
                style={{ 
                  padding: isMobile ? '6px 8px' : '8px 12px', 
                  borderBottom: `1px solid ${isUser ? 'rgba(255, 240, 240, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
                  backgroundColor: isUser ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.03)',
                  textAlign: 'left'
                }} 
                {...props}
              />
            );
          },
          td({node, ...props}) {
            return (
              <td 
                style={{ 
                  padding: isMobile ? '6px 8px' : '8px 12px', 
                  borderBottom: `1px solid ${isUser ? 'rgba(255, 240, 240, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`
                }} 
                {...props}
              />
            );
          },
          img({node, ...props}) {
            return (
              <img 
                style={{ 
                  maxWidth: '100%', 
                  height: 'auto',
                  borderRadius: '8px',
                  margin: isMobile ? '6px 0' : '8px 0'
                }} 
                loading="lazy"
                {...props} 
              />
            );
          }
        }}
      >
        {message}
      </ReactMarkdown>
    );
  }, [message, isUser, isMobile]);

  const getBackgroundColor = () => {
    if (isUser) {
      return colors.bubble.userBg;
    }
    
    switch (type) {
      case 'system':
        return '#f0f0f0';
      case 'assistant':
      default:
        return colors.bubble.assistantBg;
    }
  };

  const getTextColor = () => {
    if (isUser) {
      return colors.bubble.userText;
    }
    
    switch (type) {
      case 'system':
        return '#666';
      case 'assistant':
      default:
        return colors.bubble.assistantText;
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      margin: isMobile ? '8px 0' : '12px 0', 
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      width: '100%',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      transition: 'opacity 0.4s ease, transform 0.4s ease',
    }}
    className="message-container"
    >
      <Box sx={{ 
        width: type === 'assistant' ? '100%' : 'auto',
        maxWidth: type === 'assistant' ? '100%' : (isMobile ? '85%' : '80%'),
        minWidth: type === 'assistant' ? 'auto' : (isMobile ? '80px' : '100px'),
      }}>
        <Paper 
          elevation={type === 'system' ? 1 : 2}
          sx={{ 
            padding: isMobile ? '12px 14px' : '16px 20px', 
            borderRadius: type === 'system' ? '12px' : '20px',
            backgroundColor: getBackgroundColor(), 
            color: getTextColor(), 
            boxShadow: type === 'system' ? '0 2px 8px rgba(0,0,0,0.05)' : '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
            fontStyle: type === 'system' ? 'italic' : 'normal',
            overflow: 'hidden',
            wordWrap: 'break-word',
            '&:hover': {
              boxShadow: type === 'system' ? '0 2px 8px rgba(0,0,0,0.08)' : '0 6px 16px rgba(0,0,0,0.12)',
            }
          }}
        >
          <Box 
            sx={{ 
            fontSize: isMobile ? '0.95rem' : '1rem', 
            lineHeight: 1.6, 
            wordWrap: 'break-word',
            transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: 1,
            transform: 'translateY(0)',
            '& .math-display': {
              margin: isMobile ? '6px 0' : '8px 0',
              overflowX: 'auto'
            },
            '& .math-inline': {
              display: 'inline-block'
            }
          }}
          >
            {renderedMessage}
          </Box>
        </Paper>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: isMobile ? '0.7rem' : '0.8rem', 
            color: colors.text.light, 
            marginTop: isMobile ? '4px' : '6px', 
            textAlign: isUser ? 'right' : 'left',
            display: 'block',
            fontWeight: 500,
            paddingLeft: isMobile ? '4px' : '8px',
            paddingRight: isMobile ? '4px' : '8px',
          }}
        >
          {formatTime(timestamp)}
          {isSynced && (
            <span style={{ marginLeft: isMobile ? '4px' : '8px', fontSize: isMobile ? '0.65rem' : '0.7rem', opacity: 0.7 }}>
              🔄 同步
            </span>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default Message;
